// Справка „Гости" (Справки → reservations-guests).
//
// Един ред = един гост (един нормализиран телефон): колко пъти е резервирал,
// колко пъти е дошъл, отказал или не се е явил, колко е похарчил и кога е бил
// за последно. Групирането е на BE (`GET /reports/analytics/reservations/
// guests`) — там е и нормализацията на телефона; таблицата е клиентска.
//
// По подразбиране периодът е последните 3 месеца: гостът се познава по
// повече от една резервация, а един ден рядко има повторения.
//
// Клик на ред (или Enter върху него) отваря историята на госта за същия
// период — `GuestVisitsModal`.

import { useMemo, useState } from "react";
import { Spin } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { useViewport } from "../../../hooks/useViewport";
import { useReservationsGuestsReport } from "../../../hooks/useReports";
import { ReportDatePicker } from "../../../components/ReportDatePicker";
import { useReportsDateParam } from "../useReportsDateParam";
import { DataTable } from "../../../components/dataTable";
import type { ReservationGuestRow } from "../../../api/reportsReservations";
import ExportExcelButton from "./ExportExcelButton";
import { ReportTopBar } from "./ReportTopBar";
import { pluralLabel } from "./reportPlural";
import { GuestsKpis } from "./reservations/GuestsKpis";
import { GuestVisitsModal } from "./reservations/GuestVisitsModal";
import { useGuestsColumns } from "./reservations/guestsColumns";
import { exportGuests } from "./reservations/guestsExport";
import { summarizeGuestRows } from "./reservations/guestsMath";
import { useReservationPeriodFilters } from "./reservations/useReservationPeriodFilters";

type Props = { onOpenMobileMenu?: () => void; isNarrow?: boolean };

export const ReservationsGuestsReport = ({
  onOpenMobileMenu,
  isNarrow = false,
}: Props = {}) => {
  const { t } = useTranslation();
  const { isNarrow: viewportNarrow } = useViewport();
  const narrow = isNarrow || viewportNarrow;

  const [dateRange, setDateRange] = useReportsDateParam(() => [
    dayjs().startOf("day").subtract(3, "month"),
    dayjs().endOf("day"),
  ]);
  const [searchText, setSearchText] = useState("");
  const [visibleRows, setVisibleRows] = useState<ReservationGuestRow[]>([]);
  const [selectedGuest, setSelectedGuest] =
    useState<ReservationGuestRow | null>(null);

  const filters = useReservationPeriodFilters(dateRange);
  const { data, isLoading, isFetching, refetch } =
    useReservationsGuestsReport(filters);
  const rows = useMemo(() => data?.rows ?? [], [data]);

  // Търсачката е по име и телефон — и по нормализирания ключ, за да намери
  // „0888…" и когато е записан като „+359 888…".
  const searchedRows = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return rows;
    const needleDigits = needle.replace(/\D/g, "");
    return rows.filter(
      (row) =>
        row.guest_name.toLowerCase().includes(needle) ||
        row.phone.toLowerCase().includes(needle) ||
        (needleDigits.length > 0 && row.phone_key.includes(needleDigits)),
    );
  }, [rows, searchText]);

  const summary = useMemo(() => summarizeGuestRows(searchedRows), [searchedRows]);
  const columns = useGuestsColumns();

  const tableTitle = (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-pos-mono text-[10.5px] font-bold uppercase leading-none tracking-[0.1em] text-muted">
        {t("reportsPage.rvGuestsKicker")}
      </span>
      <span className="text-[16px] font-extrabold leading-none text-ink">
        {pluralLabel(
          t,
          visibleRows.length,
          "reportsPage.rvGuestOne",
          "reportsPage.rvGuestsMany",
        )}
        <span className="font-medium text-muted">
          {` · ${t("reportsPage.rvGuestsHint")}`}
        </span>
      </span>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-canvas">
      <ReportTopBar
        title={t("reportsPage.rvGuests")}
        isNarrow={narrow}
        onOpenMobileMenu={onOpenMobileMenu}
        onRefresh={() => refetch()}
        isFetching={isFetching}
      />

      <div
        className={`min-h-0 flex-1 overflow-y-auto overscroll-contain ${
          narrow ? "px-3.5 pb-6 pt-3.5" : "px-7 pb-8 pt-[22px]"
        }`}
      >
        <div className="flex flex-col gap-3.5">
          <div className="flex flex-wrap items-center justify-center gap-2 overflow-x-auto pb-0.5">
            <ReportDatePicker
              value={dateRange}
              onChange={setDateRange}
              defaultMode="month"
            />
          </div>

          {isLoading ? (
            <div className="grid place-items-center py-20">
              <Spin size="large" />
            </div>
          ) : (
            <>
              <GuestsKpis summary={summary} isNarrow={narrow} />

              <DataTable<ReservationGuestRow>
                tableId="reservations-guests"
                data={searchedRows}
                columns={columns}
                getRowId={(row) => row.phone_key}
                refreshing={isFetching}
                ariaLabel={t("reportsPage.rvGuests")}
                title={tableTitle}
                search={{
                  value: searchText,
                  onChange: setSearchText,
                  placeholder: t("reportsPage.rvGuestsSearchPlaceholder"),
                }}
                onFilteredRowsChange={setVisibleRows}
                onRowClick={setSelectedGuest}
                toolbarRight={
                  <ExportExcelButton
                    onExport={() => exportGuests({ rows: visibleRows, t })}
                    disabled={visibleRows.length === 0}
                    bare
                  />
                }
                // Най-редовните горе — редът, в който BE връща редовете.
                initialSorting={[{ id: "reservations", desc: true }]}
                emptyTitle={t("reportsPage.rvNoData")}
                emptyHint={t("reportsPage.rvEmptyHint")}
              />
            </>
          )}
        </div>
      </div>

      <GuestVisitsModal
        guest={selectedGuest}
        dateRange={dateRange}
        filters={filters}
        onClose={() => setSelectedGuest(null)}
      />
    </div>
  );
};
